import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import type { CellValue, Subject } from '../api';
import { iconFor } from '../subjectStyle';
import type { CellMove } from './Cell';

type CellEditorProps = {
  subjects: Subject[];
  initial: CellValue;
  /** Saves. Without a move the editor stays open (picking a subject goes on to the note); with one the sheet opens a neighbour. */
  onCommit: (value: CellValue, move?: CellMove) => void;
  /** Closes the editor. Nothing is saved by closing, so Escape restores the cell. */
  onCancel: () => void;
};

/** Which neighbour a key asks for; undefined when the key is not a movement. */
function moveFor(event: KeyboardEvent): CellMove | undefined {
  if (event.key === 'Tab') return event.shiftKey ? 'previous' : 'next';
  if (!event.ctrlKey) return undefined;
  const moves: Record<string, CellMove> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };
  return moves[event.key];
}

/**
 * The inside of an open cell. Without a subject you type to filter the subject list (Enter picks the first match);
 * with one you type the note. Ctrl+K reopens the list on a filled cell.
 */
export function CellEditor({ subjects, initial, onCommit, onCancel }: CellEditorProps) {
  const [subjectId, setSubjectId] = useState(initial.subjectId);
  const [note, setNote] = useState(initial.note);
  const [picking, setPicking] = useState(initial.subjectId === null);
  // The note input must not save on a blur caused by the editor itself: after it saved, closed, or swapped to the list.
  const settled = useRef(false);

  const commit = (value: CellValue, move?: CellMove) => onCommit(value, move);

  const showList = () => {
    settled.current = true;
    setPicking(true);
  };

  const pick = (id: string) => {
    settled.current = false;
    setSubjectId(id);
    setPicking(false);
    commit({ subjectId: id, note }); // saving on the pick is the quickest path; the note follows on Enter
  };

  function noteKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'k' && event.ctrlKey) {
      event.preventDefault();
      return showList();
    }
    if (event.key === 'Backspace' && note === '' && subjectId !== null) {
      event.preventDefault();
      setSubjectId(null);
      showList();
      return commit({ subjectId: null, note: '' });
    }
    const move = moveFor(event);
    if (event.key === 'Enter' || move) {
      event.preventDefault();
      settled.current = true;
      commit({ subjectId, note: note.trim() }, move);
      if (!move) onCancel();
    }
  }

  function noteBlur() {
    if (settled.current) return;
    settled.current = true;
    commit({ subjectId, note: note.trim() });
    onCancel();
  }

  function cancel() {
    settled.current = true;
    onCancel();
  }

  return (
    <div onKeyDown={(event) => event.key === 'Escape' && cancel()}>
      {picking ? (
        // The label names the input too: cmdk points aria-labelledby at it, which wins over an aria-label.
        <Command label="Vak" className="rounded-md border">
          <CommandInput autoFocus placeholder="Typ een vak…" />
          <CommandList className="max-h-40">
            <CommandEmpty>Geen vak gevonden.</CommandEmpty>
            {subjects.map((subject) => (
              <CommandItem key={subject.id} value={subject.name} onSelect={() => pick(subject.id)} className="min-h-9">
                <span aria-hidden="true">{iconFor(subject.icon)}</span> {subject.name}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      ) : (
        <input
          autoFocus
          value={note}
          maxLength={200}
          placeholder="Notitie (Ctrl+K: ander vak)"
          aria-label="Notitie"
          onChange={(event) => setNote(event.target.value)}
          onKeyDown={noteKeyDown}
          onBlur={noteBlur}
          className="w-full bg-transparent px-1 py-0.5 outline-none"
        />
      )}
    </div>
  );
}
